import './LicenseOptionSelect.scss';

import { type FC, useState, useMemo, useEffect } from 'react';
import classNames from 'classnames';

import { FontLicenseType } from 'client/graphql/types/operations';

import * as Core from 'components/core';

import { useTranslation } from 'react-i18next';

import usersImage from 'assets/images/users.svg';
import websitesImage from 'assets/images/websites.svg';
import { formatDecimal } from 'config/formatting';
import useLanguage from 'utils/useLanguage';

interface Props {
  options: {
    id: number;
    type: FontLicenseType;
    maxValue: number;
  }[];
  value: number | null;
  onChange(licenseOptionId: number): void;
}

const LICENSE_TYPE_ORDER: FontLicenseType[] = [
  FontLicenseType.Desktop,
  FontLicenseType.Web,
  FontLicenseType.Custom,
];

const FamiliesLicenseOptionSelect: FC<Props> = (props) => {
  const { options, value, onChange } = props;

  const { t } = useTranslation();
  const language = useLanguage();

  const [selectedLicenseType, setSelectedLicenseType] =
    useState<FontLicenseType>(LICENSE_TYPE_ORDER[0]);

  const filteredOptions = useMemo(() => {
    return options.filter((option) => option.type === selectedLicenseType);
  }, [options, selectedLicenseType]);

  useEffect(() => {
    onChange(filteredOptions[0]?.id);
  }, [onChange, filteredOptions]);

  return (
    <div className="FamiliesLicenseOptionSelect">
      <div className="FamiliesLicenseOptionSelect-primaryLicenses">
        <div className="FamiliesLicenseOptionSelect-buttons">
          {LICENSE_TYPE_ORDER.map((licenseType) => (
            <button
              key={licenseType}
              className={classNames('FamiliesLicenseOptionSelect-button', {
                'is-selected': licenseType === selectedLicenseType,
              })}
              onClick={() => {
                setSelectedLicenseType(licenseType as FontLicenseType);
              }}
            >
              {t(`licenseType.${licenseType.toLowerCase()}.name`)}
            </button>
          ))}
        </div>

        <div
          className={classNames({
            'FamiliesLicenseOptionSelect-disabledOptions':
              selectedLicenseType === FontLicenseType.Student,
          })}
        >
          {selectedLicenseType !== FontLicenseType.Student && (
            <div className="FamiliesLicenseOptionSelect-licenseDescription">
              <p>
                <span
                  dangerouslySetInnerHTML={{
                    __html: t(
                      `licenseType.${selectedLicenseType.toLowerCase()}.details`,
                    ).toString(),
                  }}
                />{' '}
                {selectedLicenseType !== FontLicenseType.Custom && (
                  <Core.Link
                    to={`/licensing#${selectedLicenseType.toLowerCase()}`}
                  >
                    {t('family.viewLicense')}
                  </Core.Link>
                )}
              </p>
            </div>
          )}

          {selectedLicenseType !== FontLicenseType.Custom &&
            selectedLicenseType !== FontLicenseType.Student && (
              <div className="FamiliesLicenseOptionSelect-select">
                <Core.Select
                  value={value ?? undefined}
                  onChange={(event) => {
                    onChange(parseInt(event.currentTarget.value));
                  }}
                >
                  {filteredOptions.map((licenseOption) => (
                    <option key={licenseOption.id} value={licenseOption.id}>
                      {t(
                        `licenseType.${licenseOption.type.toLowerCase()}.description`,
                        {
                          count: licenseOption.maxValue,
                          description: formatDecimal(
                            language,
                            licenseOption.maxValue,
                          ),
                        },
                      )}
                    </option>
                  ))}
                </Core.Select>
              </div>
            )}
        </div>
      </div>

      <div className="FamiliesLicenseOptionSelect-student">
        <button
          className={classNames(
            'FamiliesLicenseOptionSelect-button FamiliesLicenseOptionSelect-studentButton',
            {
              'is-selected': selectedLicenseType === FontLicenseType.Student,
            },
          )}
          onClick={() => {
            setSelectedLicenseType(FontLicenseType.Student);
          }}
        >
          {t('licenseType.student.name')}
        </button>

        <div
          className={classNames(
            'FamiliesLicenseOptionSelect-licenseDescription',
            {
              'FamiliesLicenseOptionSelect-disabledOptions':
                selectedLicenseType !== FontLicenseType.Student,
            },
          )}
        >
          <p>
            {t('licenseType.student.details')}{' '}
            <Core.Link to="/licensing#student">
              {t('family.viewLicense')}
            </Core.Link>
          </p>

          <div className="FamiliesLicenseOptionSelect-detailList">
            <div className="FamiliesLicenseOptionSelect-detailListItem">
              <img
                src={usersImage}
                className="FamiliesLicenseOptionSelect-detailIcon"
              />{' '}
              {t('licenseType.student.detailsUser')}
            </div>

            <div className="FamiliesLicenseOptionSelect-detailListItem">
              <img
                src={websitesImage}
                className="FamiliesLicenseOptionSelect-detailIcon"
              />{' '}
              {t('licenseType.student.detailsWebsite')}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FamiliesLicenseOptionSelect;
